import React, { useMemo, useState } from "react";
import { shuffle, pick, precisionOf, starsOf, Hud, Flash, FLASH_MS } from "./nqShared";

const ANIMALS = ["🐱", "🐶", "🐸", "🐰"];
const OBJECTS = ["🚗", "⚽", "📚", "🎩"];
const RULES = {
  couleur: { label: "Couleur", hint: "Regarde le cadre : rouge ou bleu ?", bins: [{ id: "rouge", label: "Rouge", color: "#E0705C" }, { id: "bleu", label: "Bleu", color: "#5E9BD6" }], of: (c) => c.color },
  taille: { label: "Taille", hint: "Regarde la carte : grande ou petite ?", bins: [{ id: "grand", label: "Grand", color: "#8B6FC0" }, { id: "petit", label: "Petit", color: "#F2B84B" }], of: (c) => c.size },
  categorie: { label: "Catégorie", hint: "Regarde le dessin : un animal ou un objet ?", bins: [{ id: "animal", label: "Animal", color: "#7CC6A6" }, { id: "objet", label: "Objet", color: "#B58C67" }], of: (c) => c.cat },
};
const CFG = {
  1: { cards: 12, switchEvery: 6, rules: ["couleur", "taille"], announce: true },
  2: { cards: 16, switchEvery: 4, rules: ["couleur", "taille", "categorie"], announce: true },
  3: { cards: 20, switchEvery: 3, rules: ["couleur", "taille", "categorie"], announce: false },
};

export const brief = (level) => {
  const c = CFG[level.n];
  return {
    title: "Trie les cartes… mais la règle change !",
    goal: "ranger chaque carte dans la bonne boîte.",
    regarde: `Lis la règle du moment : ${c.rules.map((r) => RULES[r].label.toLowerCase()).join(", ")}. Puis regarde la carte.`,
    fais: "Touche la boîte qui correspond à la carte. Une nouvelle carte arrive aussitôt.",
    attention: `La règle change souvent !${c.announce ? " Un message 🦎 te prévient : relis la règle avant de trier." : " Au niveau Maître, personne ne te prévient : relis la règle avant chaque carte."} ${c.cards} cartes à trier.`,
    preview: { ok: ["🐱 → 📦 Animal", "🚗 → 📦 Objet"], ko: ["Trier avec l'ancienne règle"], okLabel: "Exemple (règle « catégorie »)", koLabel: "Le piège" },
  };
};

const makeCards = (n) => Array.from({ length: n }, (_, i) => {
  const cat = Math.random() < 0.5 ? "animal" : "objet";
  return { id: i, emoji: pick(cat === "animal" ? ANIMALS : OBJECTS), cat, color: Math.random() < 0.5 ? "rouge" : "bleu", size: Math.random() < 0.5 ? "grand" : "petit" };
});
const makeRules = (cfg) => {
  const seq = []; let last = null;
  for (let i = 0; i < cfg.cards; i++) {
    if (i % cfg.switchEvery === 0) { const options = cfg.rules.filter((r) => r !== last); last = pick(options); }
    seq.push(last);
  }
  return seq;
};

export default function GameCameleon({ power, level, onFinish, practice, onPracticeEnd }) {
  const cfg = useMemo(() => (practice ? { ...CFG[level.n], cards: 4, switchEvery: 2, announce: true } : CFG[level.n]), [level, practice]);
  const cards = useMemo(() => shuffle(makeCards(cfg.cards)), [cfg.cards]);
  const rules = useMemo(() => makeRules(cfg), [cfg]);
  const [idx, setIdx] = useState(0);
  const [stats, setStats] = useState({ ok: 0, ko: 0, switchKo: 0 });
  const [feedback, setFeedback] = useState(null);
  const card = cards[idx];
  const rule = RULES[rules[idx]];
  const justSwitched = idx > 0 && rules[idx] !== rules[idx - 1];

  const drop = (binId) => {
    if (feedback) return;
    const ok = rule.of(card) === binId;
    setStats((s) => ({ ok: s.ok + (ok ? 1 : 0), ko: s.ko + (ok ? 0 : 1), switchKo: s.switchKo + (!ok && justSwitched ? 1 : 0) }));
    setFeedback(ok ? "ok" : "ko");
    setTimeout(() => {
      setFeedback(null);
      if (idx + 1 >= cards.length) {
        if (practice) return onPracticeEnd();
        const total = cards.length; const hits = stats.ok + (ok ? 1 : 0); const errors = stats.ko + (ok ? 0 : 1);
        const precision = precisionOf(hits, total, 0);
        return onFinish({ hits, total, errors, precision, stars: starsOf(precision), detail: `${hits}/${total} cartes bien triées · ${stats.switchKo + (!ok && justSwitched ? 1 : 0)} erreur(s) juste après un changement de règle` });
      }
      return setIdx(idx + 1);
    }, FLASH_MS);
  };

  return (
    <div className="space-y-4" data-testid="nq-game-cameleon">
      <Hud items={[["Carte", `${idx + 1}/${cards.length}`, power.color], ["Bien triées", stats.ok, "#7CC6A6"], ["Erreurs", stats.ko, "#E0705C"]]} />
      <div className="relative rounded-3xl bg-[#FFF3D6] p-5 sm:p-6 space-y-5">
        <Flash kind={feedback} koLabel="✗ Mauvaise boîte !" />
        <div className="text-center">
          {cfg.announce && justSwitched && <div className="inline-block mb-2 px-4 py-1.5 rounded-full bg-[#8B6FC0] text-white text-sm font-bold animate-fade-up" data-testid="nq-rule-change">🦎 La règle change !</div>}
          <div className="text-xs uppercase tracking-[0.25em] font-bold text-[var(--ink-soft)]">Règle du moment</div>
          <div className="font-serif-title text-2xl sm:text-3xl text-[var(--ink)]" data-testid="nq-rule">Trie par {rule.label.toLowerCase()}</div>
          <div className="text-xs text-[var(--ink-soft)] mt-0.5">{rule.hint}</div>
        </div>
        <div className="flex justify-center">
          <div key={idx} data-testid="nq-card" className={`animate-fade-up rounded-3xl bg-white shadow-lg border-4 flex items-center justify-center transition-all ${card.size === "grand" ? "w-36 h-36 sm:w-44 sm:h-44" : "w-24 h-24 sm:w-28 sm:h-28"}`}
            style={{ borderColor: card.color === "rouge" ? "#E0705C" : "#5E9BD6", background: card.color === "rouge" ? "#FDE4DF" : "#E3EEF9" }}>
            <span className={card.size === "grand" ? "text-7xl sm:text-8xl" : "text-4xl sm:text-5xl"}>{card.emoji}</span>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-4">
          {rule.bins.map((b) => (
            <button key={b.id} onClick={() => drop(b.id)} data-testid={`nq-bin-${b.id}`}
              className="rounded-3xl py-6 text-white text-xl font-black shadow-md active:scale-95 hover:brightness-105 transition-all" style={{ background: b.color }}>📦 {b.label}</button>
          ))}
        </div>
      </div>
    </div>
  );
}
